import { FunctionComponent, MouseEvent } from "react";

import { ReactComponent as PhoneSvg } from "@assets/icons/phone.svg";

import { EmailIcon, EmailIconProps } from "@/components/EmailIcon/EmailIcon";
import { PhoneIcon } from "@/components/PhoneIcon/PhoneIcon";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { SvgIcon } from "@/components/Icon/SvgIcon";

import { ListPersonHeaderQuickEnrich } from "./listPersonHeader.types";

const stopClickThrough = (e: MouseEvent): void => {
	e.stopPropagation();
};

interface ListPersonHeaderFallbackIconsProps {
	linkedinUrl?: string | null;
	email?: string | null;
	emailIconStatus: EmailIconProps["status"];
	phone?: string | null;
	handleCopyClick: (value: string) => void;
	quickEnrich?: ListPersonHeaderQuickEnrich;
}

/**
 * Read-only channel icons for the no-`domain` fallback of {@link ListPersonHeader}
 * (surfaces that don't wire the unified {@link ContactChannels} row). With
 * `quickEnrich` set — the Chrome extension's not-in-Signalist card — the email
 * and phone icons become click-to-enrich buttons running the add-then-search
 * chain with a searching spinner; otherwise they stay a plain copy / `tel:` link.
 */
export const ListPersonHeaderFallbackIcons: FunctionComponent<
	ListPersonHeaderFallbackIconsProps
> = ({ linkedinUrl, email, emailIconStatus, phone, handleCopyClick, quickEnrich }) => (
	<>
		{linkedinUrl && (
			<a
				href={linkedinUrl}
				referrerPolicy="no-referrer"
				rel="noreferrer"
				target="_blank"
				onClick={stopClickThrough}
			>
				<LinkedinIcon status="checked" />
			</a>
		)}
		{quickEnrich ? (
			<>
				<button
					aria-label="email"
					className="inline-flex cursor-pointer disabled:cursor-not-allowed"
					disabled={quickEnrich.disabled}
					type="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						quickEnrich.onEnrichEmail();
					}}
				>
					<EmailIcon status={quickEnrich.emailSearching ? "searching" : "refresh"} />
				</button>
				<button
					aria-label="phone"
					className="inline-flex cursor-pointer disabled:cursor-not-allowed"
					disabled={quickEnrich.disabled}
					type="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						quickEnrich.onEnrichPhone();
					}}
				>
					<PhoneIcon status={quickEnrich.phoneSearching ? "searching" : "refresh"} />
				</button>
			</>
		) : (
			<>
				<button
					aria-label="copy email"
					className="inline-flex cursor-pointer"
					type="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();

						if (email) {
							handleCopyClick(email);
						}
					}}
				>
					<EmailIcon status={emailIconStatus} />
				</button>
				{phone && (
					<a
						href={`tel:${phone}`}
						referrerPolicy="no-referrer"
						rel="noreferrer"
						target="_blank"
						onClick={stopClickThrough}
					>
						<SvgIcon className="mr-1 h-[16px] w-auto" svgIcon={PhoneSvg} />
					</a>
				)}
			</>
		)}
	</>
);
